/**
 * Draws DICOM Overlay Planes (group 60xx) on top of a Cornerstone viewport.
 *
 * Cornerstone3D has no overlay-plane renderer, so scanner-drawn graphics —
 * Siemens AutoAlign spine level lines and labels, for one — are decoded and
 * then dropped. This layer paints them back, in a 2D canvas above the
 * viewport's own canvas, transformed so the overlay tracks the image through
 * pan, zoom, rotate and flip.
 *
 * A 2D canvas (not a second WebGL viewport) is deliberate: the viewer is
 * already close to the browser's WebGL context ceiling with many study tabs
 * open, and an overlay must not cost a context.
 *
 * See DicomOverlayPlaneService for the tag layout and extraction.
 */

import React, { useCallback, useContext, useEffect, useRef } from "react";
import { getEnabledElement, Enums as CoreEnums } from "@cornerstonejs/core";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import {
  DICOM_OVERLAY_COLOR,
  computeOverlayTransform,
  getImageFrameIndex,
  getOverlayBitmap,
  getOverlayFrameIndex,
  getOverlayPlanes,
} from "../../helpers/DicomOverlayPlaneService";

const DicomOverlayLayer = ({ viewportElement }) => {
  const { showDicomOverlay } = useContext(ViewportContext);
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const showRef = useRef(showDicomOverlay);
  showRef.current = showDicomOverlay;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !viewportElement) return;

    const hide = () => {
      if (canvas.style.display !== "none") {
        canvas.style.display = "none";
        // Release the backing store — one of these exists per viewport.
        canvas.width = 0;
        canvas.height = 0;
      }
    };

    if (!showRef.current) {
      hide();
      return;
    }

    let viewport;
    try {
      viewport = getEnabledElement(viewportElement)?.viewport;
    } catch (_) {
      viewport = null;
    }
    // Volume/MPR viewports have no single source instance to read overlays from.
    if (typeof viewport?.getCurrentImageId !== "function") {
      hide();
      return;
    }

    const imageId = viewport.getCurrentImageId();
    const planes = getOverlayPlanes(imageId);
    if (!planes.length) {
      hide();
      return;
    }

    const imageData = viewport.getImageData();
    if (!imageData?.origin || !imageData?.direction || !imageData?.spacing) {
      hide();
      return;
    }

    const rect = viewportElement.getBoundingClientRect();
    const cssWidth = Math.max(1, Math.round(rect.width));
    const cssHeight = Math.max(1, Math.round(rect.height));
    const dpr = window.devicePixelRatio || 1;
    const deviceWidth = Math.round(cssWidth * dpr);
    const deviceHeight = Math.round(cssHeight * dpr);

    if (canvas.width !== deviceWidth || canvas.height !== deviceHeight) {
      canvas.width = deviceWidth;
      canvas.height = deviceHeight;
    }
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.style.display = "block";

    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const imageFrameIndex = getImageFrameIndex(imageId);

    const worldToCanvas = (world) => viewport.worldToCanvas(world);

    planes.forEach((plane) => {
      const overlayFrame = getOverlayFrameIndex(plane, imageFrameIndex);
      if (overlayFrame < 0) return;

      const t = computeOverlayTransform(imageData, plane, worldToCanvas);
      if (!t) return;

      const bitmap = getOverlayBitmap(
        imageId,
        plane,
        overlayFrame,
        DICOM_OVERLAY_COLOR
      );
      if (!bitmap) return;

      // Zoomed in, nearest-neighbour keeps the 1-bit graphics crisp. Zoomed
      // out, it would drop single-pixel lines entirely, so interpolate.
      ctx.imageSmoothingEnabled = Math.hypot(t.a, t.b) < 1;
      // worldToCanvas returns CSS pixels; scale the whole transform to device
      // pixels rather than pre-scaling the context.
      ctx.setTransform(
        t.a * dpr,
        t.b * dpr,
        t.c * dpr,
        t.d * dpr,
        t.e * dpr,
        t.f * dpr
      );
      ctx.drawImage(bitmap, 0, 0);
    });

    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }, [viewportElement]);

  // Coalesce the render-event storm during pan/zoom into one draw per frame.
  const scheduleDraw = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      draw();
    });
  }, [draw]);

  useEffect(() => {
    if (!viewportElement) return undefined;

    scheduleDraw();

    const events = [
      CoreEnums.Events.IMAGE_RENDERED,
      CoreEnums.Events.CAMERA_MODIFIED,
      CoreEnums.Events.STACK_NEW_IMAGE,
    ];
    events.forEach((event) =>
      viewportElement.addEventListener(event, scheduleDraw)
    );

    // A layout change can resize the element without triggering a re-render
    // event, which would leave the canvas at the old size.
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleDraw);
      observer.observe(viewportElement);
    }

    return () => {
      events.forEach((event) =>
        viewportElement.removeEventListener(event, scheduleDraw)
      );
      observer?.disconnect();
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [viewportElement, scheduleDraw]);

  // Toggling visibility does not raise a viewport event.
  useEffect(() => {
    scheduleDraw();
  }, [showDicomOverlay, scheduleDraw]);

  return (
    <canvas
      ref={canvasRef}
      className="dicom-overlay-plane-layer"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        // Above the viewport image, below the annotation and spine-label
        // layers (z-index 200+) so user markings stay on top.
        zIndex: 1,
        pointerEvents: "none",
        display: "none",
      }}
    />
  );
};

export default DicomOverlayLayer;
